import { useRef, type RefObject } from 'react';

/**
 * search-scroll-dismiss-keyboard — a finger that moves more than this (px,
 * vertically) is a drag, not a tap: it closes the keyboard. A tap is never
 * perfectly still; below the platform tap slop (Android 8 dp, iOS ~10 pt) it
 * must stay a tap, so a result is picked at the first touch.
 */
export const DRAG_DISMISS_PX = 10;

/**
 * Closes the soft keyboard when the user drags over a scrollable screen while
 * its search field has the focus. Spread the handlers onto the scroll
 * container. Blurring the field takes the keyboard down on iOS and Android,
 * no native plugin needed (a no-op when the field is not focused).
 *
 * The finger, not the `scroll` event: iOS scrolls on its own to keep the
 * focused field visible, which would close the keyboard while typing.
 */
export function useDismissKeyboardOnDrag(inputRef: RefObject<HTMLInputElement | null>) {
  const touchStartY = useRef<number | null>(null);

  const closeKeyboard = () => inputRef.current?.blur();

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartY.current == null) return;
    if (Math.abs(e.touches[0].clientY - touchStartY.current) <= DRAG_DISMISS_PX) return;
    touchStartY.current = null;
    closeKeyboard();
  };

  return { onTouchStart, onTouchMove, closeKeyboard };
}
